import React, {createContext, useCallback, useContext} from "react";
import {IconButton, Tooltip} from "@mui/material";
import UpdateIcon from "@mui/icons-material/Update";
import {EmployeeRef, KeyData} from "../../api/Model";
import {usePermission} from "../../util/access/UseAccess";
import {
    Employees_Read,
    KeyData_Read,
    Neighbourhoods_Read,
    Teams_Read,
    WorkSites_Read,
} from "../../util/access/Permission";

export interface EmployeeKeyHistoryDialogContextType {
    openEmployeeKeyHistoryDialog: (employee: EmployeeRef, origin: {x: number, y: number}) => Promise<void>;
}

export const EmployeeKeyHistoryDialogContext = createContext<EmployeeKeyHistoryDialogContextType | null>(null);

export interface KeyHistoryDialogContextType {
    openKeyHistoryDialog: (keyData: KeyData, origin: {x: number, y: number}) => void;
}

export const KeyHistoryDialogContext = createContext<KeyHistoryDialogContextType | null>(null);

export function useEmployeeKeyHistoryDialog() {
    const context = useContext(EmployeeKeyHistoryDialogContext);
    if (!context) {
        throw new Error('useEmployeeKeyHistoryDialog must be used within an EmployeeKeyHistoryDialogProvider');
    }
    return context.openEmployeeKeyHistoryDialog;
}

export function useKeyHistoryDialog() {
    const context = useContext(KeyHistoryDialogContext);
    if (!context) {
        throw new Error('useKeyHistoryDialog must be used within a KeyHistoryDialogProvider');
    }
    return context.openKeyHistoryDialog;
}

export function EmployeeHistoryButton({employee}: {employee: EmployeeRef}) {
    const {canViewHistory} = usePermission({
        canViewHistory: [Employees_Read, KeyData_Read, Neighbourhoods_Read, Teams_Read, WorkSites_Read],
    });
    const openEmployeeKeyHistoryDialog = useEmployeeKeyHistoryDialog();

    const handleOpenDialog = useCallback((event: React.MouseEvent) => {
        void openEmployeeKeyHistoryDialog(employee, {x: event.clientX, y: event.clientY});
    }, [employee, openEmployeeKeyHistoryDialog]);

    if (!canViewHistory) return null;

    return (
        <Tooltip title={`Key history for ${employee.fullName}`}>
            <IconButton
                size="small"
                color="primary"
                onClick={handleOpenDialog}
                aria-label={`Key history for ${employee.fullName}`}
            >
                <UpdateIcon fontSize="small"/>
            </IconButton>
        </Tooltip>
    );
}

export function KeyHistoryButton({keyData}: {keyData: KeyData}) {
    const openKeyHistoryDialog = useKeyHistoryDialog();

    const handleOpenDialog = useCallback((event: React.MouseEvent) => {
        openKeyHistoryDialog(keyData, {x: event.clientX, y: event.clientY});
    }, [keyData, openKeyHistoryDialog]);

    return (
        <Tooltip title={`Key history for key ${keyData.keyNumber}`}>
            <IconButton
                size="small"
                color="primary"
                onClick={handleOpenDialog}
                aria-label={`Key history for key ${keyData.keyNumber}`}
            >
                <UpdateIcon fontSize="small"/>
            </IconButton>
        </Tooltip>
    );
}
